import { Checkpoints, Checkpoint } from "src/constants/Checkpoints";
import { Planets, Planet } from "src/constants/Planets";
import Exploring1 from "src/pages/student/scenes/Exploring1";
import SpaceStation1 from "src/pages/student/scenes/SpaceStation1";
import SpaceStation2 from "src/pages/student/scenes/SpaceStation2";
import SpaceStation3 from "src/pages/student/scenes/SpaceStation3";
import SpaceStation4 from "src/pages/student/scenes/SpaceStation4";
import SpaceStation5 from "src/pages/student/scenes/SpaceStation5";
import StudentPostGame from "../pages/student/scenes/StudentPostGame";
import StudentPreGame from "../pages/student/scenes/StudentPreGame";
import StudentActivity from "../pages/student/scenes/StudentActivity";
import StudentReward from "../pages/student/scenes/StudentReward";
import SpaceStationComplete from "src/pages/student/scenes/SpaceStationComplete";
import Landing1 from "../pages/student/scenes/Landing1";
import Landing2 from "src/pages/student/scenes/Landing2";
import Leaving1 from "src/pages/student/scenes/Leaving1";
import Leaving2 from "src/pages/student/scenes/Leaving2";
import { BaseProps, EnhancedProps } from "src/pages/student/models/props";
import StudentGame from "src/pages/student/scenes/StudentGame";
import PlanetClose from "src/pages/student/scenes/PlanetClose";
import StudentVideo from "src/pages/student/scenes/StudentVideo";
import MissionLog from "../pages/student/scenes/MissionLog";
import SpaceStationCeremony from "src/pages/student/scenes/SpaceStationCeremony";

export interface CheckStepResult {
    checkpointId: number;
    stepId: number;
    planetId: number;
}

export class LevelConfig {
    /** Made into a class purely for convenience and naming. This gives a hardcoded description of a given checkpoint */
    checkpoint: Checkpoint;
    step: number;
    component: React.ComponentType<EnhancedProps>;
}

export interface Level {
    planet: Planet;
    checkpoint: Checkpoint;
    step: number;
    component: React.ComponentType<EnhancedProps>;

    order: number;
}

export default class SceneManager {
    private static Levels: Level[] = [];
    public static Default: LevelConfig = { checkpoint: Checkpoints.None, step: 0, component: null };
    public static PrePlanetLevels: LevelConfig[] = [
        { checkpoint: Checkpoints.Onboarding, step: 1, component: SpaceStation1 },
        { checkpoint: Checkpoints.Onboarding, step: 2, component: SpaceStation2 },
        { checkpoint: Checkpoints.Onboarding, step: 3, component: SpaceStation3 },
        { checkpoint: Checkpoints.Onboarding, step: 4, component: SpaceStation4 },
        { checkpoint: Checkpoints.Onboarding, step: 5, component: SpaceStation5 },
    ];
    public static PlanetLevels: LevelConfig[] = [
        { checkpoint: Checkpoints.Landing, step: 1, component: Landing1 },
        { checkpoint: Checkpoints.Landing, step: 2, component: Landing2 },
        { checkpoint: Checkpoints.Exploring, step: 1, component: Exploring1 },

        { checkpoint: Checkpoints.Video, step: 1, component: StudentVideo },
        { checkpoint: Checkpoints.PreGame, step: 1, component: StudentPreGame },
        { checkpoint: Checkpoints.Game, step: 1, component: StudentGame },
        { checkpoint: Checkpoints.PostGame, step: 1, component: StudentPostGame },

        { checkpoint: Checkpoints.Activity, step: 1, component: StudentActivity },

        { checkpoint: Checkpoints.Badge, step: 1, component: StudentReward },
        { checkpoint: Checkpoints.PlanetClose, step: 1, component: PlanetClose },

        { checkpoint: Checkpoints.Leaving, step: 1, component: Leaving1 },
        { checkpoint: Checkpoints.Leaving, step: 2, component: Leaving2 },
        { checkpoint: Checkpoints.BetweenPlanets, step: 1, component: MissionLog },
    ];
    public static PostPlanetLevels: LevelConfig[] = [
        { checkpoint: Checkpoints.GalaxyCeremony, step: 1, component: SpaceStationCeremony },
        { checkpoint: Checkpoints.GalaxyComplete, step: 1, component: SpaceStationComplete },
    ];

    public static All: LevelConfig[] = [
        ...SceneManager.PrePlanetLevels,
        ...SceneManager.PlanetLevels,
        ...SceneManager.PostPlanetLevels,
    ];

    public static FindByIds(checkpointId: number, stepId: number): LevelConfig {
        return this.All.find(x => x.checkpoint.id === checkpointId && x.step === stepId);
    }

    public static CompileLevelState(): void {
        // Need to make a list of all of the possible steps
        // This will need to know about the planets, checkpoints and where the loops are
        let masterCounter = 1;
        let levels: Level[] = [];

        // Pre loop levels
        this.PrePlanetLevels.forEach(x => {
            levels.push({
                ...x,
                planet: Planets.R,
                order: masterCounter++,
            });
        });

        // Loop levels
        [...Planets.All].sort((a, b) => a.order - b.order).forEach(planet => {
            this.PlanetLevels.forEach(level => {
                levels.push({
                    ...level,
                    planet: planet,
                    order: masterCounter++,
                });
            });
        });

        // Post loop levels
        this.PostPlanetLevels.forEach(x => {
            levels.push({
                ...x,
                planet: Planets.C,
                order: masterCounter++,
            });
        });

        this.Levels = levels;
    }

    public static GetCurrentLevel(checkpointId: number, stepNumber: number, planetId: number): Level {
        if (this.Levels == null || this.Levels.length === 0) {
            this.CompileLevelState();
        }

        /** First Planet */
        let _minPlanet: Planet = Planets.R;
        /** Last Planet */
        let _maxPlanet: Planet = Planets.C;

        let _planetId = planetId;
        let _checkpointId = checkpointId;
        let _step = stepNumber;

        // Get current level
        let currentLevel = this.Levels.find(x => x.planet.id === _planetId && x.checkpoint.id === _checkpointId && x.step === _step);
        if (currentLevel == null) {
            // Failed. Sanity checking time!
            // JB: Given the changes on the backend, this should literally never happen
            console.warn("Broken level", { planetId: planetId, checkpointId: checkpointId, stepId: stepNumber });

            if (Planets.FindById(_planetId) === Planets.Default) {
                // Broken planet
                _planetId = _planetId < _minPlanet.id ? _minPlanet.id : _maxPlanet.id;
            }
            if (Checkpoints.FindById(_checkpointId) === Checkpoints.None) {
                // Broken checkpoint
                _checkpointId = _checkpointId < Checkpoints.Onboarding.id ? Checkpoints.Onboarding.id : Checkpoints.Landing.id;
            }
            // Reset step
            _step = 1;

            currentLevel = this.Levels.find(x => x.planet.id === _planetId && x.checkpoint.id === _checkpointId && x.step === _step);
            if (currentLevel == null) {
                // Well shit, this shouldn't happen
                console.error("Error when handling scene transition");
                currentLevel = this.Levels[0];
            }
        }

        return currentLevel;
    }

    public static GetNextStep(checkpointId: number, stepNumber: number, planetId: number): CheckStepResult {
        let currentLevel = this.GetCurrentLevel(checkpointId, stepNumber, planetId);
        let nextLevel = this.Levels.find(x => x.order === currentLevel.order + 1);
        nextLevel = nextLevel || this.Levels[this.Levels.length - 1];
        return { planetId: nextLevel.planet.id, checkpointId: nextLevel.checkpoint.id, stepId: nextLevel.step };
    }

    public static GetPreviousStep(checkpointId: number, stepNumber: number, planetId: number): CheckStepResult {
        let currentLevel = this.GetCurrentLevel(checkpointId, stepNumber, planetId);
        let nextLevel = this.Levels.find(x => x.order === currentLevel.order - 1);
        nextLevel = nextLevel || this.Levels[0];
        return { planetId: nextLevel.planet.id, checkpointId: nextLevel.checkpoint.id, stepId: nextLevel.step };
    }

    public static GetNextStepOld(checkpointId: number, stepNumber: number, planetId: number): CheckStepResult {
        let nextLevel: LevelConfig = null;

        let _stepNumber: number = stepNumber;
        let _planet = Planets.FindById(planetId);
        let _checkpoint = Checkpoints.FindById(checkpointId);

        let _maxStep: number = 5;
        /** Start of the loop */
        let _minCheckpoint: Checkpoint = Checkpoints.Landing;
        /** End of the loop */
        let _maxCheckpoint: Checkpoint = Checkpoints.BetweenPlanets;
        /** First Planet */
        let _minPlanet: Planet = Planets.R;
        /** Last Planet */
        let _maxPlanet: Planet = Planets.C;

        // Special considerations
        // Sanity checks
        _planet = _planet == null || _planet.order < 1 ? Planets.R : _planet;
        _checkpoint = _checkpoint == null || _checkpoint.order < 1 ? Checkpoints.Onboarding : _checkpoint;
        _stepNumber = _stepNumber == null || _stepNumber < 1 ? 0 : _stepNumber; // Default to 0 because steps are incremented

        // Leaving last planet, or are completely passed it for some reason
        if ((_planet.order > _maxPlanet.order) ||
            (_planet.order === _maxPlanet.order && _checkpoint.order >= _maxCheckpoint.order)) {
            // Send to GalaxyComplete
            return { checkpointId: Checkpoints.GalaxyComplete.id, stepId: 1, planetId: _maxPlanet.id };
        }

        do {
            _stepNumber++;
            if (_stepNumber > _maxStep) {
                _checkpoint = Checkpoints.GetNext(_checkpoint);
                _stepNumber = 1;
                if (_checkpoint.order > _maxCheckpoint.order) {
                    _planet = Planets.GetNext(_planet);
                    _checkpoint = _minCheckpoint;
                    if (_planet.order > _maxPlanet.order) {
                        // If we make it here, something really broke, like a broken checkpoint or a step that got mangled
                        // Send to GalaxyComplete
                        return { checkpointId: Checkpoints.GalaxyComplete.id, stepId: 1, planetId: _maxPlanet.id };
                    }
                }
            }

            nextLevel = this.FindByIds(_checkpoint.id, _stepNumber);
        } while (nextLevel == null);

        return { checkpointId: _checkpoint.id, stepId: _stepNumber, planetId: _planet.id };
    }

    public static GetPreviousStepOld(checkpointId: number, stepNumber: number, planetId: number): CheckStepResult {
        let nextLevel: LevelConfig = null;

        let _stepNumber: number = stepNumber;
        let _planet = Planets.FindById(planetId);
        let _checkpoint = Checkpoints.FindById(checkpointId);

        let _maxStep: number = 5;
        /** Start of the loop */
        let _minCheckpoint: Checkpoint = Checkpoints.Landing;
        /** End of the loop */
        let _maxCheckpoint: Checkpoint = Checkpoints.BetweenPlanets;
        /** First Planet */
        let _minPlanet: Planet = Planets.R;
        /** Last Planet */
        let _maxPlanet: Planet = Planets.C;

        // Special considerations
        // Sanity checks
        _planet = _planet == null || _planet.order < 1 ? Planets.R : _planet;
        _checkpoint = _checkpoint == null || _checkpoint.order < 1 ? Checkpoints.Onboarding : _checkpoint;
        _stepNumber = _stepNumber == null || _stepNumber < 1 ? 0 : _stepNumber; // Default to 0 because steps are incremented

        // Previous on low level items doesnt make sense last planet, or are completely passed it for some reason
        if (_planet.order < _minPlanet.order) {
            // Send to start
            return { checkpointId: Checkpoints.Onboarding.id, stepId: 1, planetId: _minPlanet.id };
        }

        do {
            _stepNumber--;
            if (_stepNumber < 1) {
                _checkpoint = Checkpoints.GetPrevious(_checkpoint);
                _stepNumber = _maxStep;
                if (_checkpoint.order < _minCheckpoint.order) {
                    _planet = Planets.GetPrevious(_planet);
                    _checkpoint = _maxCheckpoint;
                    if (_planet.order < _minPlanet.order) {
                        // If we make it here, something really broke, like a broken checkpoint or a step that got mangled
                        // Send to GalaxyComplete
                        return { checkpointId: Checkpoints.Onboarding.id, stepId: 1, planetId: _minPlanet.id };
                    }
                }
            }

            nextLevel = this.FindByIds(_checkpoint.id, _stepNumber);
        } while (nextLevel == null);

        return { checkpointId: _checkpoint.id, stepId: _stepNumber, planetId: _planet.id };
    }
}




// WEBPACK FOOTER //
// ./src/constants/SceneManager.ts